
import * as React from 'react';
import { Table, TableProps, Row, Col } from 'antd';
import { SortOrder, TablePaginationConfig } from 'antd/es/table/interface';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';

export interface ICbTableParams {
    search?: string;
    sortCol: string;
    sortDir: string;
}

export interface ICbPagingItems {
    currentPage: number;
    pageSize: number;
}

interface CbTableWithPaginationProps<T> extends Omit<TableProps<T>, 'onChange' | 'pagination'> {
    totalRecords?: number;
    setTableParams: React.Dispatch<React.SetStateAction<ICbTableParams>>;
    pagingItems?: ICbPagingItems;
    setPagingItems: React.Dispatch<React.SetStateAction<ICbPagingItems>>;
    paginationClassName?: string;
    tablePageSize?: number;
    pageSizeOptions?: string[];
    showQuickJumper?: boolean; 
    onTableChange?: (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<T> | SorterResult<T>[]) => void;
}

const IconUp = React.memo(() => (
    <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 4L4 1L7 4" stroke="#0255D2" />
    </svg>
));

const IconDown = React.memo(() => (
    <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 1L4 4L1 1" stroke="#0255D2" />
    </svg>
));

const SortIcons = ({ sortOrder }: { sortOrder: SortOrder }) => {
    if (sortOrder === "ascend") {
        return <Row align="middle" style={{ marginLeft: 6 }}><IconUp /></Row>;
    } else if (sortOrder === "descend") {
        return <Row align="middle" style={{ marginLeft: 6 }}><IconDown /></Row>;
    } else {
        return (
            <Row align="middle" style={{ marginLeft: 6 }}>
                <Col span={24}>
                    <Row align="middle" style={{ marginBottom: 2 }}>
                        <IconUp />
                    </Row>
                    <Row align="middle">
                        <IconDown />
                    </Row>
                </Col>
            </Row>
        );
    }
};

const addSortIconsToColumns = (columns: any[]): any[] => {
    return columns.map((column) => ({
        ...column,
        sortIcon: column.sorter
            ? (props: { sortOrder: SortOrder }): React.ReactNode => <SortIcons sortOrder={props.sortOrder} />
            : undefined,
        children: column.children ? addSortIconsToColumns(column.children) : undefined
    }));
};

export const defaultTablePageSize = 15;

const CbTableWithPagination = <T extends object>({
    scroll,
    virtual = false,
    className = '',
    style: externalStyles = {},
    paginationClassName = "",
    totalRecords = 0,
    setTableParams,
    setPagingItems,
    pagingItems,
    tablePageSize = defaultTablePageSize,
    pageSizeOptions = ['15', '50', '100', '200'],
    showQuickJumper = false, 
    onTableChange,
    locale = {
        triggerDesc: "",
        triggerAsc: "",
        cancelSort: "",
    },
    ...props
}: CbTableWithPaginationProps<T>) => {

    const currentPageSize = pagingItems?.pageSize ?? tablePageSize;

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<T> | SorterResult<T>[]
    ) => {
        const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

        setTableParams((prev) => ({
            ...prev,
            sortCol: singleSorter?.columnKey ? String(singleSorter.columnKey) : prev.sortCol,
            sortDir: singleSorter?.order === "descend" ? "desc" : singleSorter?.order === "ascend" ? "asc" : prev.sortDir,
        }));

        if (pagination?.current && pagination?.pageSize) {
            setPagingItems({
                currentPage: pagination.current,
                pageSize: pagination.pageSize
            });
        }

        if (onTableChange) {
            onTableChange(pagination, filters, sorter);
        }
    };

    const memoizedColumns = React.useMemo(
        () => addSortIconsToColumns(props.columns ?? []),
        [props.columns]
    );

    const scrollConfig = scroll ?? {
        y: currentPageSize > 15 ? window.innerHeight - 300 : undefined
    };

    return (
        <Table<T>
            {...props}
            className={`Tabel-style ${className}`}
            style={{ overflowX: "clip", ...externalStyles }}
            scroll={scrollConfig}
            locale={locale}
            pagination={{
                total: totalRecords,
                current: pagingItems?.currentPage ?? 1,
                pageSize: currentPageSize,
                className: paginationClassName,
                position: ["bottomRight"],
                pageSizeOptions: pageSizeOptions,
                showSizeChanger: true,
                showQuickJumper: showQuickJumper,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                onChange: (page: number, pageSize: number) => {
                    setPagingItems({
                        currentPage: page,
                        pageSize: pageSize
                    });
                },
            }}
            onChange={handleTableChange}
            columns={memoizedColumns}
            virtual={virtual}
        />
    );
};

export default CbTableWithPagination;